import {List} from 'immutable';
import {Observable} from 'rxjs';

import RESTClient from 'web-app/api/clients/rest-client-famly-app';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import * as SentryService from 'web-app/services/sentry';
import * as NewPostHelpers from 'web-app/react/feed/post/new-post/helpers';

import Draft from './model';
import * as Queries from './queries';
import * as Constants from './constants';
import {ObservationVariant} from '../observations/constants';

export const createPost = (
    recipients,
    from = '',
    body = '',
    richTextBody = '',
    imageIds,
    videoIds,
    fileIds,
    send = true,
    disableFeedback,
    options,
    activities,
    famlyLiveMeetingId,
) => {
    if (!from) {
        // The backend is getting events where from is not set.
        // We can't reproduce so try to log this to see what happens in sentry.
        SentryService.captureMessage('From not set');
        return Observable.throw('From not set');
    }

    // We only send one type of body argument. Rich text takes precendence
    const bodyArgument = richTextBody
        ? {richTextBody: NewPostHelpers.addActivitiesToBody(richTextBody, activities)}
        : {body: NewPostHelpers.addActivitiesToBody(body, activities)};

    return RESTClient.postAuthenticatedObservable(
        'v2/posts',
        {
            recipients,
            from,
            ...bodyArgument,
            imageIds,
            fileIds,
            send,
            videoId: videoIds[0], // TODO: Change when API accepts an array instead
            disableFeedback,
            famlyMeetingId: famlyLiveMeetingId,
        },
        options,
    ).map(result => ({
        draft: Draft.fromAPI(result),
    }));
};

export const updateDraft = (
    postId,
    recipients,
    from = '',
    body = '',
    richTextBody = '',
    imageIds,
    videoIds,
    fileIds,
    send = false,
    disableFeedback,
    options,
    activities,
    famlyLiveMeetingId,
    facebookPageIds,
    rssInstitutionSetIds,
    overridePostBlock,
    sendAsSms,
) => {
    if (!from) {
        // The backend is getting events where from is not set.
        // We can't reproduce so try to log this to see what happens in sentry.
        SentryService.captureMessage('From not set');
        return Observable.throw('From not set');
    }

    // We only send one type of body argument. Rich text takes precendence
    const bodyArgument = richTextBody
        ? {richTextBody: NewPostHelpers.addActivitiesToBody(richTextBody, activities)}
        : {body: NewPostHelpers.addActivitiesToBody(body, activities)};

    return RESTClient.postAuthenticatedObservable(
        `v2/posts/${postId}`,
        {
            recipients,
            from,
            ...bodyArgument,
            imageIds,
            fileIds,
            send,
            videoId: videoIds[0], // TODO: Change when API accepts an array instead
            disableFeedback,
            famlyMeetingId: famlyLiveMeetingId,
            facebookPages: facebookPageIds,
            rssInstSetIds: rssInstitutionSetIds,
            overridePostBlock,
            sendAsSms,
        },
        options,
    ).map(result => ({
        draft: Draft.fromAPI(result),
    }));
};

export const getDrafts = () => {
    return RESTClient.getAuthenticatedObservable('v2/posts/drafts').map(draftsData => ({
        drafts: List(draftsData).map(draft => Draft.fromAPI(draft, false)),
    }));
};

export const deleteDraft = postId => {
    return RESTClient.deleteAuthenticatedObservable(`v2/posts/${postId}`);
};

export const fetchPost = postId =>
    RESTClient.getAuthenticatedObservable(`v2/posts/${postId}`).map(result => Draft.fromAPI(result.post));

export const deleteObservationDraft = observationId => {
    return Observable.fromPromise(
        getApolloClient().mutate({
            mutation: Queries.DeleteObservation,
            variables: {
                observationIds: [observationId],
            },
        }),
    );
};

export const getObservationDrafts = (
    author,
    variants = [ObservationVariant.REGULAR_OBSERVATION, ObservationVariant.ASSESSMENT],
    childIds = null,
) => {
    return Observable.fromPromise(
        getApolloClient().query({
            query: Queries.Observations,
            variables: {
                authors: [author],
                statuses: [Constants.ObservationStatusState.DRAFT],
                variants,
                childIds: childIds ? childIds.split(',') : null,
            },
            fetchPolicy: 'network-only',
            context: {
                verifyRequest: true,
            },
        }),
    ).map(({data}) => ({
        observationDrafts: List(data.childDevelopment.observations.results).map(obs => Draft.fromAPI(obs, true)),
    }));
};

const createOrUpdateObservationObservable = (
    observationId,
    childIds,
    body = '',
    nextStep = '',
    statements = [],
    areaRefinements = [],
    imageIds,
    videoIds,
    fileIds,
    variant,
    status,
    observedAt,
    options,
) => {
    const requestObservable = Observable.fromPromise(
        getApolloClient().mutate({
            mutation: Queries.CreateObservation,
            variables: {
                observationId,
                childIds: childIds ? childIds.split(',') : [],
                remark: {
                    body,
                    statements,
                    date: observedAt,
                    areas: areaRefinements,
                },
                nextStep: {
                    body: nextStep,
                    statements: [],
                    areas: [],
                },
                imageIds,
                videoId: videoIds[0], // TODO: Change when API accepts an array instead
                fileIds,
                status,
                variant,
            },
        }),
    ).map(({data}) => ({
        draft: Draft.fromAPI(data.childDevelopment.observations.createObservation, true),
    }));

    if (options && options.timeout) {
        return Observable.race(
            requestObservable,
            Observable.timer(options.timeout).mergeMap(() => Observable.throw(new Error('Timeout'))),
        );
    }

    return requestObservable;
};

export const createOrUpdateObservationDraft = (
    observationId,
    childId,
    body,
    nextStep,
    statements,
    areaRefinements,
    imageIds,
    videoIds,
    fileIds,
    variant,
    observedAt,
    options,
) => {
    return createOrUpdateObservationObservable(
        observationId,
        childId,
        body,
        nextStep,
        statements,
        areaRefinements,
        imageIds,
        videoIds,
        fileIds,
        variant,
        Constants.ObservationStatusState.DRAFT,
        observedAt,
        options,
    );
};

export const sendObservation = (
    observationId,
    childId,
    body,
    nextStep,
    statements,
    areaRefinements,
    imageIds,
    videoIds,
    fileIds,
    variant,
    status = Constants.ObservationStatusState.SENT,
    observedAt,
) => {
    return createOrUpdateObservationObservable(
        observationId,
        childId,
        body,
        nextStep,
        statements,
        areaRefinements,
        imageIds,
        videoIds,
        fileIds,
        variant,
        status,
        observedAt,
    );
};
